import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Send } from 'lucide-react';
import { BatteryIcon } from './BatteryIcon';
import { ComingSoonModal } from './ComingSoonModal';

interface HeroProps {
  percentage: number;
  isCharging: boolean;
}

export const Hero: React.FC<HeroProps> = ({ percentage, isCharging }) => {
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleBuyClick = () => {
    setShowComingSoon(true);
    setTimeout(() => setShowComingSoon(false), 1500);
  };
  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-4 md:px-6 3xl:px-8 4xl:px-12 5xl:px-16 pt-20 md:pt-24 3xl:pt-28 4xl:pt-32 5xl:pt-36 pb-8 md:pb-12 3xl:pb-16 4xl:pb-20 5xl:pb-24 relative overflow-hidden">
      
      {/* Background Ambient Glow - Extremely subtle now */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[500px] md:h-[500px] 3xl:w-[600px] 3xl:h-[600px] 4xl:w-[700px] 4xl:h-[700px] 5xl:w-[800px] 5xl:h-[800px] bg-white/[0.01] blur-[80px] md:blur-[100px] 3xl:blur-[120px] 4xl:blur-[140px] 5xl:blur-[160px] rounded-full pointer-events-none" />

      {/* Pigeon Placeholder with Sleepy Levitation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className="relative z-10 mb-4 md:mb-6 3xl:mb-8 4xl:mb-10 5xl:mb-12"
      >
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          {/* Pigeon Container */}
          <div className="group w-48 h-48 md:w-64 md:h-64 3xl:w-80 3xl:h-80 4xl:w-96 4xl:h-96 5xl:w-[28rem] 5xl:h-[28rem] bg-[#0a0a0a] rounded-3xl border border-white/10 flex items-center justify-center relative overflow-hidden cursor-pointer">
            <img 
              src="/pigeon.svg" 
              alt="Low Battery Pigeon" 
              className="object-contain w-full h-full opacity-50 transition-all duration-700 group-hover:opacity-70 group-hover:scale-110"
            />
            {/* Dark overlay to ensure "low power" feel */}
            <div className="absolute inset-0 bg-black/40 transition-opacity duration-700 group-hover:bg-black/20" />
            
            {/* ZZZ animation overlay */}
            <motion.div 
              className="absolute top-4 right-6 md:top-4 md:right-8 3xl:top-6 3xl:right-12 4xl:top-8 4xl:right-16 5xl:top-10 5xl:right-20 font-light text-white/30 text-3xl md:text-4xl 3xl:text-5xl 4xl:text-6xl 5xl:text-7xl transition-all duration-700 group-hover:text-white/60 group-hover:scale-110"
              animate={{ opacity: [0, 0.8, 0], y: [0, -30], x: [0, 10] }}
              transition={{ duration: 4, repeat: Infinity, delay: 0 }}
            >z</motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Center Battery - No Fixed Positioning */}
      <div className="mb-10 md:mb-12 3xl:mb-16 4xl:mb-20 5xl:mb-24 z-10">
        <BatteryIcon percentage={percentage} isCharging={isCharging} />
        <div className="text-center mt-4 md:mt-5 3xl:mt-6 4xl:mt-7 5xl:mt-8">
             <span className="text-[10px] md:text-xs 3xl:text-xs 4xl:text-sm 4xl:text-base 5xl:text-lg text-neutral-600 font-mono uppercase tracking-[0.2em]">
                {percentage <= 20 ? "Critical Level" : "Energy Saving Mode"}
             </span>
        </div>
      </div>

      {/* Text Content */}
      <div className="text-center z-10 max-w-xl md:max-w-2xl 3xl:max-w-3xl 4xl:max-w-4xl 5xl:max-w-5xl mx-auto">
        <motion.h2 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-3xl md:text-4xl 3xl:text-5xl 4xl:text-6xl 5xl:text-7xl font-thin tracking-tighter text-white mb-4 md:mb-6 3xl:mb-8 4xl:mb-10 5xl:mb-12 lowercase"
        >
          too tired to rug.
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="text-sm md:text-base 3xl:text-lg 4xl:text-xl 5xl:text-2xl text-neutral-500 font-light tracking-wide mb-6 md:mb-8 3xl:mb-10 4xl:mb-12 5xl:mb-14 lowercase"
        >
          the only solana token powered by pure exhaustion.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 3xl:gap-6 4xl:gap-8 5xl:gap-10"
        >
          <button 
            onClick={handleBuyClick}
            className="w-full sm:w-auto px-6 md:px-8 3xl:px-10 4xl:px-12 5xl:px-14 py-2 md:py-3 3xl:py-4 4xl:py-5 5xl:py-6 bg-danger/10 text-danger border border-danger/30 rounded-xl hover:bg-danger hover:text-white transition-all duration-300 flex items-center justify-center gap-2 relative"
          >
            <span className="font-normal tracking-wide text-xs md:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl">buy $1pct</span>
            <ArrowRight className="w-3 h-3 md:w-3 md:h-3 3xl:w-4 3xl:h-4 4xl:w-5 4xl:h-5 5xl:w-6 5xl:h-6" />
          </button>

          <button 
            onClick={() => window.open('https://t.me/LowBatteryPigeon', '_blank')}
            className="w-full sm:w-auto px-6 md:px-8 3xl:px-10 4xl:px-12 5xl:px-14 py-2 md:py-3 3xl:py-4 4xl:py-5 5xl:py-6 bg-white/5 text-neutral-400 border border-white/5 rounded-xl hover:bg-white/10 hover:text-neutral-200 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <span className="font-normal tracking-wide text-xs md:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl">telegram</span>
            <Send className="w-3 h-3 md:w-3 md:h-3 3xl:w-4 3xl:h-4 4xl:w-5 4xl:h-5 5xl:w-6 5xl:h-6 opacity-70" />
          </button>
        </motion.div>
        
        {/* Coming soon modal */}
        <ComingSoonModal isVisible={showComingSoon} />
      </div>
    </section>
  );
};